import { apiClient, type Paginated } from '@/core/api';
import type { LessonAssetKind } from '@/features/curriculum';
import type {
  CreateProjectLinkArtifactInput,
  GradeProjectInput,
  ProjectArtifactKind,
  ProjectAssetDto,
  ProjectDetailDto,
  ProjectListDto,
  ProjectListParams,
  ProjectMemberDto,
  ProjectSubmissionArtifactDto,
  ProjectSubmissionDto,
  ProjectSubmissionListParams,
  ProjectTaskDto,
  ProjectTaskListParams,
  ProjectTaskWriteInput,
  ProjectWriteDto,
  ProjectWriteInput,
  ReorderTasksInput,
  TaskCommentDto,
  TaskCommentListParams,
} from './types';

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

/** `GET /api/projects/` — admin: all; teacher: their classes'; pupil: only
 *  published projects they are ON THE TEAM of. Parents get 403. */
export async function getProjects(
  params: ProjectListParams = {}
): Promise<Paginated<ProjectListDto>> {
  const { data } = await apiClient.get<Paginated<ProjectListDto>>('/projects/', { params });
  return data;
}

/** `GET /api/projects/{public_id}/` — the brief arrives read-enriched. */
export async function getProject(publicId: string): Promise<ProjectDetailDto> {
  const { data } = await apiClient.get<ProjectDetailDto>(`/projects/${publicId}/`);
  return data;
}

/** `POST /api/projects/` — admin or a teacher of the target class. Answers with
 *  the WRITE shape (bare `school_class`, un-enriched blocks). */
export async function createProject(input: ProjectWriteInput): Promise<ProjectWriteDto> {
  const { data } = await apiClient.post<ProjectWriteDto>('/projects/', input);
  return data;
}

/** `PATCH /api/projects/{public_id}/` — saving the brief sends the FULL blocks
 *  array (the server replaces the document). Answers with the WRITE shape. */
export async function updateProject(
  publicId: string,
  input: ProjectWriteInput
): Promise<ProjectWriteDto> {
  const { data } = await apiClient.patch<ProjectWriteDto>(`/projects/${publicId}/`, input);
  return data;
}

/** `POST /api/projects/{public_id}/publish/`. */
export async function publishProject(publicId: string): Promise<ProjectDetailDto> {
  const { data } = await apiClient.post<ProjectDetailDto>(`/projects/${publicId}/publish/`);
  return data;
}

/** `POST /api/projects/{public_id}/unpublish/`. */
export async function unpublishProject(publicId: string): Promise<ProjectDetailDto> {
  const { data } = await apiClient.post<ProjectDetailDto>(`/projects/${publicId}/unpublish/`);
  return data;
}

/** `POST /api/projects/{public_id}/archive/` — freezes the board (C4). */
export async function archiveProject(publicId: string): Promise<ProjectDetailDto> {
  const { data } = await apiClient.post<ProjectDetailDto>(`/projects/${publicId}/archive/`);
  return data;
}

/** `POST /api/projects/{public_id}/duplicate/` — a fresh draft, optionally in
 *  another class. The team, the board and the result never carry over. */
export async function duplicateProject(
  publicId: string,
  schoolClass?: string
): Promise<ProjectDetailDto> {
  const { data } = await apiClient.post<ProjectDetailDto>(
    `/projects/${publicId}/duplicate/`,
    schoolClass ? { school_class: schoolClass } : {}
  );
  return data;
}

/**
 * `POST /api/projects/{public_id}/members/` — the posted list REPLACES the
 * whole team (backend ADR-017). Members are school Member public_ids (roster
 * ids), not team-seat ids. Removed members keep their cards, unassigned.
 */
export async function replaceProjectMembers(
  publicId: string,
  members: string[]
): Promise<ProjectDetailDto> {
  const { data } = await apiClient.post<ProjectDetailDto>(`/projects/${publicId}/members/`, {
    members,
  });
  return data;
}

/** `GET /api/project-members/?project=` — the team seats of one project. */
export async function getProjectMembers(project: string): Promise<Paginated<ProjectMemberDto>> {
  const { data } = await apiClient.get<Paginated<ProjectMemberDto>>('/project-members/', {
    params: { project, page_size: 50 },
  });
  return data;
}

/** `POST /api/project-assets/` — staff multipart upload with progress; the
 *  ProjectAsset mirror of `/lesson-assets/` (same kinds and limits). */
export async function uploadProjectAsset(
  kind: LessonAssetKind,
  file: File,
  onProgress?: (percent: number) => void
): Promise<ProjectAssetDto> {
  const form = new FormData();
  form.append('kind', kind);
  form.append('file', file);
  const { data } = await apiClient.post<ProjectAssetDto>('/project-assets/', form, {
    onUploadProgress: event => {
      if (onProgress && event.total) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    },
  });
  return data;
}

// ---------------------------------------------------------------------------
// Task board
// ---------------------------------------------------------------------------

/** `GET /api/project-tasks/?project=` — team members and staff. */
export async function getProjectTasks(
  params: ProjectTaskListParams = {}
): Promise<Paginated<ProjectTaskDto>> {
  const { data } = await apiClient.get<Paginated<ProjectTaskDto>>('/project-tasks/', { params });
  return data;
}

/** `POST /api/project-tasks/` — pupils on the team may create. */
export async function createProjectTask(input: ProjectTaskWriteInput): Promise<ProjectTaskDto> {
  const { data } = await apiClient.post<ProjectTaskDto>('/project-tasks/', input);
  return data;
}

/** `PATCH /api/project-tasks/{public_id}/` — move a card, assign it, edit it. */
export async function updateProjectTask(
  publicId: string,
  input: ProjectTaskWriteInput
): Promise<ProjectTaskDto> {
  const { data } = await apiClient.patch<ProjectTaskDto>(`/project-tasks/${publicId}/`, input);
  return data;
}

/** `DELETE /api/project-tasks/{public_id}/` — STAFF ONLY (a pupil moving a
 *  card must never be able to erase the team's history). */
export async function deleteProjectTask(publicId: string): Promise<void> {
  await apiClient.delete(`/project-tasks/${publicId}/`);
}

/** `POST /api/project-tasks/reorder/` — `tasks` must list every task of the
 *  project exactly once; the server rejects partial boards. */
export async function reorderProjectTasks(input: ReorderTasksInput): Promise<ProjectTaskDto[]> {
  const { data } = await apiClient.post<ProjectTaskDto[]>('/project-tasks/reorder/', input);
  return data;
}

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

/** `GET /api/task-comments/?task=`. */
export async function getTaskComments(
  params: TaskCommentListParams = {}
): Promise<Paginated<TaskCommentDto>> {
  const { data } = await apiClient.get<Paginated<TaskCommentDto>>('/task-comments/', { params });
  return data;
}

/** `POST /api/task-comments/` — any team member or staff of the class. */
export async function createTaskComment(task: string, body: string): Promise<TaskCommentDto> {
  const { data } = await apiClient.post<TaskCommentDto>('/task-comments/', { task, body });
  return data;
}

/** `PATCH /api/task-comments/{public_id}/` — AUTHOR ONLY (405 otherwise). */
export async function updateTaskComment(publicId: string, body: string): Promise<TaskCommentDto> {
  const { data } = await apiClient.patch<TaskCommentDto>(`/task-comments/${publicId}/`, { body });
  return data;
}

/** `DELETE /api/task-comments/{public_id}/` — the author, or any staff. */
export async function deleteTaskComment(publicId: string): Promise<void> {
  await apiClient.delete(`/task-comments/${publicId}/`);
}

// ---------------------------------------------------------------------------
// The team deliverable
// ---------------------------------------------------------------------------

/** `GET /api/project-submissions/` — the staff review queue reads it with
 *  `needs_grading`. */
export async function getProjectSubmissions(
  params: ProjectSubmissionListParams = {}
): Promise<Paginated<ProjectSubmissionDto>> {
  const { data } = await apiClient.get<Paginated<ProjectSubmissionDto>>('/project-submissions/', {
    params,
  });
  return data;
}

/**
 * `POST /api/project-submissions/` `{project}` — get-or-create of the team's
 * single shared deliverable (201 when created, 200 when it already existed).
 *
 * TEAM MEMBERS ONLY: the server resolves visibility through `visible_to_pupil`,
 * so staff calling this get 400 `project_not_available`. Staff read the row
 * through the list endpoint instead (`getProjectSubmissions({project})`).
 */
export async function openProjectSubmission(project: string): Promise<ProjectSubmissionDto> {
  const { data } = await apiClient.post<ProjectSubmissionDto>('/project-submissions/', {
    project,
  });
  return data;
}

/** `PATCH /api/project-submissions/{public_id}/` — any team member edits the
 *  shared text while draft/returned; 400 `submission_graded` once graded. */
export async function updateProjectSubmissionText(
  publicId: string,
  text: string
): Promise<ProjectSubmissionDto> {
  const { data } = await apiClient.patch<ProjectSubmissionDto>(
    `/project-submissions/${publicId}/`,
    { text }
  );
  return data;
}

/** `POST /api/project-submissions/{public_id}/submit/` — hand in or resubmit. */
export async function submitProjectSubmission(publicId: string): Promise<ProjectSubmissionDto> {
  const { data } = await apiClient.post<ProjectSubmissionDto>(
    `/project-submissions/${publicId}/submit/`
  );
  return data;
}

/** `POST /api/project-submissions/{public_id}/grade/` — `score` for `team`
 *  mode, `grades` covering every member for `individual`. */
export async function gradeProjectSubmission(
  publicId: string,
  input: GradeProjectInput
): Promise<ProjectSubmissionDto> {
  const { data } = await apiClient.post<ProjectSubmissionDto>(
    `/project-submissions/${publicId}/grade/`,
    input
  );
  return data;
}

/** `POST /api/project-submissions/{public_id}/return/` — feedback required. */
export async function returnProjectSubmission(
  publicId: string,
  feedback: string
): Promise<ProjectSubmissionDto> {
  const { data } = await apiClient.post<ProjectSubmissionDto>(
    `/project-submissions/${publicId}/return/`,
    { feedback }
  );
  return data;
}

// ---------------------------------------------------------------------------
// Deliverable artifacts (team-owned: an upload XOR a link)
// ---------------------------------------------------------------------------

/** `POST /api/project-submission-artifacts/` — multipart upload with progress. */
export async function uploadProjectArtifact(
  submissionId: string,
  kind: Exclude<ProjectArtifactKind, 'link'>,
  file: File,
  onProgress?: (percent: number) => void
): Promise<ProjectSubmissionArtifactDto> {
  const form = new FormData();
  form.append('submission', submissionId);
  form.append('kind', kind);
  form.append('file', file);
  const { data } = await apiClient.post<ProjectSubmissionArtifactDto>(
    '/project-submission-artifacts/',
    form,
    {
      onUploadProgress: event => {
        if (onProgress && event.total) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      },
    }
  );
  return data;
}

/** `POST /api/project-submission-artifacts/` with `{kind: 'link', url}`. */
export async function createProjectLinkArtifact(
  input: CreateProjectLinkArtifactInput
): Promise<ProjectSubmissionArtifactDto> {
  const { data } = await apiClient.post<ProjectSubmissionArtifactDto>(
    '/project-submission-artifacts/',
    { submission: input.submission, kind: 'link', url: input.url }
  );
  return data;
}

/** `DELETE /api/project-submission-artifacts/{public_id}/` — a team member
 *  while the deliverable is not graded; staff anytime. */
export async function deleteProjectArtifact(publicId: string): Promise<void> {
  await apiClient.delete(`/project-submission-artifacts/${publicId}/`);
}
